import { HHTextField } from '@hinge-health/react-component-library';
import Autocomplete from '@mui/material/Autocomplete';
import { useState } from 'react';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { BillingType, useBulkUpdateBillingTypeMutation } from '../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../utils/bills-utils';
import {
  ActionTypeDisplayText,
  GridLevelName,
} from '../grid-components/grid-types';
import { PanelAlert } from '../grid-components/panel-alert';
import { PanelLayout } from '../grid-components/panel-layout';
import { PanelTitle } from '../grid-components/panel-title';
import { ClientInsurerIndexRow } from './client-insurer-index-utils';
import { SelectedContractsGrid } from './selected-contracts-grid';

export interface UpdateBillingTypePanelProps {
  selectedRows: ClientInsurerIndexRow[];
  billingTypes: BillingType[];
  onClearSelections: () => void;
  onSuccess: () => void;
}

export const UpdateBillingTypePanel = ({
  selectedRows,
  billingTypes,
  onClearSelections,
  onSuccess,
}: UpdateBillingTypePanelProps): JSX.Element => {
  const { setIsOpen, setTitle } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();

  const [panelRows, setPanelRows] =
    useState<ClientInsurerIndexRow[]>(selectedRows);
  const [selectedBillingType, setSelectedBillingType] = useState<string | null>(
    null,
  );

  const [bulkUpdateBillingType, { loading }] = useBulkUpdateBillingTypeMutation(
    {
      update(cache) {
        cache.evict({ fieldName: 'getContractsV2' });
        cache.gc();
      },
    },
  );

  const handleSubmit = async (): Promise<void> => {
    if (!selectedBillingType || panelRows.length === 0) return;

    const contractIds = panelRows
      .map(r => r.contractId)
      .filter((id): id is number => id !== null);

    try {
      await bulkUpdateBillingType({
        variables: {
          input: { contractIds, billingType: selectedBillingType },
        },
      });
      setSnackbarState(
        `Successfully updated billing type for ${contractIds.length} contract(s)`,
        'success',
        5000,
      );
      onSuccess();
      onClearSelections();
      setTimeout(() => setIsOpen(false), 500);
    } catch {
      setSnackbarState('Failed to update billing type', 'error', 5000);
    }
  };

  const handleDeleteRow = (ciId: number): void => {
    const updated = panelRows.filter(r => r.id !== ciId);
    setPanelRows(updated);
    setTitle(
      <PanelTitle
        actionType={ActionTypeDisplayText.UPDATE_BILLING_TYPE}
        rowCount={updated.length}
        gridLevel={GridLevelName.CLIENT_INSURER}
      />,
    );
  };

  const billingTypeOptions = billingTypes.map(bt => ({
    value: bt.value,
    label: capitalizeFirstLetterAndSpaceSnakeString(bt.value),
  }));

  return (
    <PanelLayout
      selectedGrid={
        <SelectedContractsGrid rows={panelRows} onDeleteRow={handleDeleteRow} />
      }
      alert={<PanelAlert drawerMode="update-billing-type" />}
      additionalInputs={
        <Autocomplete
          options={billingTypeOptions}
          getOptionLabel={(option): string => option.label}
          isOptionEqualToValue={(option, value): boolean =>
            option.value === value.value
          }
          size="small"
          blurOnSelect
          onChange={(_, value): void =>
            setSelectedBillingType(value?.value ?? null)
          }
          renderInput={(params): JSX.Element => (
            <HHTextField
              {...params}
              hhVariant="variant-bypass"
              variant="outlined"
              label="New Billing Type"
              fullWidth
            />
          )}
        />
      }
      setIsOpen={setIsOpen}
      isSubmitting={loading}
      isInvalid={!selectedBillingType || panelRows.length === 0}
      actionLabel="Update Billing Type"
      handleSubmit={handleSubmit}
    />
  );
};
